import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./OpportunityDeleteDialog.css";

export default function OpportunityDeleteDialog({
  open,
  onClose,
  opportunity,
  onDeleted,
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const rawOpportunity = useMemo(
    () => opportunity?.__raw ?? opportunity ?? null,
    [opportunity]
  );

  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open]);

  const handleDelete = async () => {
    if (!rawOpportunity?.id) return;

    setError("");
    setDeleting(true);

    try {
      await axiosInstance.delete(`/opportunities/${rawOpportunity.id}/`);
      onDeleted?.();
      onClose?.();
    } catch (e) {
      setError(
        "Cannot delete this opportunity because it is linked to other records. Please remove or update those dependencies first."
      );
    } finally {
      setDeleting(false);
    }
  };

  const opportunityName =
    rawOpportunity?.title_opportunity ||
    rawOpportunity?.code_opportunity ||
    rawOpportunity?.project_name ||
    "Unknown Opportunity";

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="opportunity-delete-dialog"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {!error && (
        <DialogTitle className="od-header">
          <Box className="od-header-content">
            <Box className="od-title-section">
              <WarningAmberIcon className="od-warning-icon" />
              <Typography variant="h5" component="span" className="od-title">
                Delete Opportunity
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              className="od-close-btn"
              size="small"
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent className="od-content">
        {error ? (
          <Alert
            severity="error"
            className="od-error-alert"
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body1" fontWeight={500}>
              Unable to Delete Opportunity
            </Typography>
            <Typography variant="body2" className="od-error-message">
              {error}
            </Typography>
          </Alert>
        ) : (
          <Box className="od-confirm-content">
            <Box className="od-opportunity-container">
              <Typography variant="subtitle2" className="od-opportunity-label">
                Opportunity
              </Typography>
              <Typography variant="h6" className="od-opportunity-value">
                {opportunityName}
              </Typography>
            </Box>

            <Typography variant="body1" className="od-warning-text">
              This action is permanent and cannot be undone.
            </Typography>

            <Box className="od-consequences">
              <Typography variant="body2" className="od-consequence-item">
                • All associated opportunity data may be affected
              </Typography>
              <Typography variant="body2" className="od-consequence-item">
                • Related offers, references or project links may no longer be available
              </Typography>
              <Typography variant="body2" className="od-consequence-item">
                • This change cannot be reversed
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="od-actions">
        {error ? (
          <Button
            onClick={onClose}
            variant="contained"
            className="od-btn od-btn-ok"
          >
            Got It
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={deleting}
              variant="outlined"
              className="od-btn od-btn-cancel"
            >
              Cancel
            </Button>

            <Button
              onClick={handleDelete}
              disabled={deleting || !rawOpportunity?.id}
              variant="contained"
              className="od-btn od-btn-delete"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              autoFocus
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}